import { useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography } from "@mui/material";
import Input from "@components/Input/Input";
import Select from "@components/Select/Select";
import { paginationSx } from "./styles";

type PaginationProps = {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
};

export function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15, 20, 25],
}: PaginationProps) {
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize],
  );

  const [pageInput, setPageInput] = useState<string>(String(page));

  useEffect(() => {
    setPageInput(String(page));
  }, [page, pageSize, totalPages]);

  const commitPage = (raw: string) => {
    const n = Number(raw);
    const candidate = Number.isFinite(n) && n > 0 ? n : 1;
    const clamped = Math.min(Math.max(1, candidate), totalPages);
    setPageInput(String(clamped)); // reflect the clamped value in the input
    onPageChange(clamped);
  };

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pt: 1, px: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1">Rows per page</Typography>
        <Select
          testId="rows-per-page-select"
          value={String(pageSize)}
          placeholder="Select"
          style={{ width: 90 }}
          options={pageSizeOptions.map((opt) => ({
            value: opt,
            label: String(opt),
          }))}
          onChange={(value) => {
            const newSize = Number(value);
            onPageChange(1);
            onPageSizeChange(newSize);
          }}
        />
        <Typography variant="body1">from {totalCount}</Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          size="small"
          data-testid="pagination-prev"
          disabled={page === 1}
          onClick={() => page > 1 && onPageChange(page - 1)}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Input
          size="small"
          value={pageInput}
          type="number"
          testId="pagination-page-input"
          sx={paginationSx}
          inputProps={{
            min: 1,
            max: totalPages,
          }}
          onChange={(e) => {
            setPageInput(e.target.value);
          }}
          onBlur={(e) => {
            commitPage(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitPage((e.target as HTMLInputElement).value);
            }
          }}
        />

        <IconButton
          size="small"
          data-testid="pagination-next"
          disabled={page === totalPages}
          onClick={() => page < totalPages && onPageChange(page + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
